import { useState } from 'react'

import { GreyGrey0, Box, Flex } from '@sequencehq/core-components'
import { match } from 'ts-pattern'
import { CustomerInfoReviewSource, CustomerSnapshot } from '../../types'
import { ReviewStep } from './steps'
import { SourceEmailHeader } from 'Automations/watchtower/inbox/manualTasks/components'
import { CustomerCardComparison } from './CustomerCardComparison'
import { BillingContactComparison } from './BillingContactComparison'

/**
 * Left ("preview") pane of the review modal: a before/after comparison scoped to
 * the current step — the customer record on the customer step, the billing
 * contacts on the contacts step. The "after" cards are driven by the draft the
 * form edits, so they update live as the operator types.
 *
 * When the change came from a forwarded email, a collapsible subheading at the
 * top attributes it to that email (sender + received time) and expands to show
 * the original subject and body (see `SourceEmailHeader`).
 */
export const CustomerChangeReview = ({
  step,
  before,
  after,
  source,
  activeContactId
}: {
  step: ReviewStep
  before: CustomerSnapshot
  after: CustomerSnapshot
  source?: CustomerInfoReviewSource
  /** On the contacts step, the focused contact whose `after` card is ringed. */
  activeContactId?: string
}) => {
  // While the forwarded email is expanded the comparison below is dimmed, so
  // the operator's attention stays on the email. It stays scrollable — the
  // dim is purely visual.
  const [isSourceExpanded, setIsSourceExpanded] = useState(false)

  return (
    <Flex
      flexDirection="column"
      height="100%"
      width="100%"
      overflow="hidden"
      backgroundColor={GreyGrey0}
    >
      {source && (
        <Box flexShrink={0}>
          <SourceEmailHeader
            source={source}
            onExpandedChange={setIsSourceExpanded}
            data-testid="customerInfoReview.sourceHeader"
          />
        </Box>
      )}

      <Box
        flex="1"
        overflowY="auto"
        padding="24px"
        opacity={isSourceExpanded ? 0.4 : 1}
        transition="opacity 0.2s ease-in-out"
        data-testid="customerInfoReview.comparison"
      >
        {/* Centre the comparison vertically within the pane; the wrapper keeps a
            full-height min so short content sits in the middle, while taller
            content still scrolls. */}
        <Flex minHeight="100%" alignItems="center" justifyContent="center">
          <Flex flexDirection="column" gap="24px" width="100%" maxWidth="720px">
            {match(step)
              .with('customer', () => (
                <CustomerCardComparison before={before} after={after} />
              ))
              .with('contacts', () => (
                <BillingContactComparison
                  before={before}
                  after={after}
                  activeContactId={activeContactId}
                />
              ))
              .exhaustive()}
          </Flex>
        </Flex>
      </Box>
    </Flex>
  )
}
